import React, { useState } from 'react';
import { ValidationSummary as ValidationSummaryType, ValidationIssue } from '../../types/reportTypes';

interface ValidationSummaryProps {
  summary: ValidationSummaryType;
  importType: 'sales_data' | 'ad_spend_data';
}

const ValidationSummary: React.FC<ValidationSummaryProps> = ({ summary, importType }) => {
  const [showAllIssues, setShowAllIssues] = useState(false);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  const handleToggleRow = (rowIndex: number) => {
    setExpandedRows((prev) => 
      prev.includes(rowIndex) 
        ? prev.filter(r => r !== rowIndex) 
        : [...prev, rowIndex]
    );
  };

  // Sort issues by row index
  const sortedIssues = [...summary.issues].sort((a, b) => a.rowIndex - b.rowIndex);
  
  // Display at most 5 issues initially, or all if showAllIssues is true
  const displayIssues = showAllIssues ? sortedIssues : sortedIssues.slice(0, 5);
  
  const getImportTypeLabel = (type: string) => {
    return type === 'sales_data' ? 'Sales Data' : 'Ad Spend Data';
  };

  return (
    <div className="bg-white rounded-lg shadow p-4 mb-4">
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        {getImportTypeLabel(importType)} Validation Results
      </h3>
      
      {/* Summary Statistics */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-gray-50 p-3 rounded-md">
          <span className="text-sm text-gray-500">Total Rows</span>
          <div className="text-xl font-semibold">{summary.total_rows}</div>
        </div>
        <div className="bg-green-50 p-3 rounded-md">
          <span className="text-sm text-green-600">Processed Rows</span>
          <div className="text-xl font-semibold text-green-700">{summary.processed_rows}</div>
        </div>
        <div className={`p-3 rounded-md ${summary.skipped_rows > 0 ? 'bg-yellow-50' : 'bg-gray-50'}`}>
          <span className={`text-sm ${summary.skipped_rows > 0 ? 'text-yellow-600' : 'text-gray-500'}`}>
            Skipped Rows
          </span>
          <div className={`text-xl font-semibold ${summary.skipped_rows > 0 ? 'text-yellow-700' : 'text-gray-700'}`}>
            {summary.skipped_rows}
          </div>
        </div>
      </div>
      
      {/* Processing Status */}
      {summary.has_issues ? (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                {summary.issues.length} validation {summary.issues.length === 1 ? 'issue' : 'issues'} found. Data was processed with warnings.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-green-50 border-l-4 border-green-400 p-3 mb-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">
                All data processed successfully!
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Issue List */}
      {summary.has_issues && (
        <div>
          <h4 className="font-medium text-gray-700 mb-2">Validation Issues:</h4>
          <div className="max-h-60 overflow-y-auto">
            <ul className="divide-y divide-gray-200">
              {displayIssues.map((issue: ValidationIssue) => (
                <li key={issue.rowIndex} className="py-2">
                  <button 
                    className="flex justify-between w-full text-left"
                    onClick={() => handleToggleRow(issue.rowIndex)}
                  >
                    <span className={expandedRows.includes(issue.rowIndex) ? "font-medium" : ""}>
                      Row {issue.rowIndex + 1} ({issue.issues.length} {issue.issues.length === 1 ? 'issue' : 'issues'})
                    </span>
                    <svg 
                      className={`h-5 w-5 transform ${expandedRows.includes(issue.rowIndex) ? 'rotate-180' : ''}`}
                      xmlns="http://www.w3.org/2000/svg" 
                      viewBox="0 0 20 20" 
                      fill="currentColor"
                    >
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {expandedRows.includes(issue.rowIndex) && (
                    <ul className="mt-2 ml-4 text-sm text-gray-600 list-disc">
                      {issue.issues.map((message, idx) => (
                        <li key={idx}>{message}</li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
          
          {/* Show More/Less Toggle */}
          {summary.issues.length > 5 && (
            <button
              className="mt-3 text-sm text-blue-600 hover:text-blue-800"
              onClick={() => setShowAllIssues(!showAllIssues)}
            >
              {showAllIssues ? 'Show Less' : `Show All (${summary.issues.length})`}
            </button>
          )}
        </div>
      )}
      
      {/* Empty Row Information */}
      {summary.skipped_rows > 0 && (
        <div className="mt-4 text-sm text-gray-600">
          <p>
            <span className="font-medium">Note:</span> {summary.skipped_rows} empty {summary.skipped_rows === 1 ? 'row was' : 'rows were'} detected and skipped automatically.
          </p>
        </div>
      )}
    </div>
  );
};

export default ValidationSummary;